import { useTranslation } from 'react-i18next';

export default function ProductNarrative() {
  const { t } = useTranslation();
  return (
    <section id="narrative" className="py-16 md:py-24 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #0c0814 0%, #100b1e 100%)' }}>
      <div className="absolute inset-0 grid-bg pointer-events-none opacity-50" />
      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
        <div className="text-center mb-10 md:mb-16">
          <div className="badge-purple mb-6">{t('productNarrative.badge')}</div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5 font-heading">{t('productNarrative.title')}</h2>
          <p className="text-base md:text-lg text-white/45 max-w-3xl mx-auto">{t('productNarrative.desc')}</p>
        </div>
        <div className="rounded-2xl md:rounded-3xl p-6 md:p-12 lg:p-14 text-white mb-10 md:mb-14 relative overflow-hidden border border-purple-400/18"
          style={{ background: 'linear-gradient(135deg, rgba(147,51,234,0.1), rgba(124,58,237,0.06))' }}>
          <div className="absolute inset-0 grid-bg-dense pointer-events-none opacity-25" />
          <div className="relative z-10 text-center max-w-5xl mx-auto">
            <h3 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-8 md:mb-12 leading-tight font-heading">
              AI + Prediction Market<br />+ PolyAi Claw + Quant Trading
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {[
                { icon: 'ri-line-chart-line', titleKey: 'productNarrative.item0_title', subKey: 'productNarrative.item0_sub' },
                { icon: 'ri-shield-check-line', titleKey: 'productNarrative.item1_title', subKey: 'productNarrative.item1_sub' },
                { icon: 'ri-robot-line', titleKey: 'productNarrative.item2_title', subKey: 'productNarrative.item2_sub' },
              ].map(item => (
                <div key={item.titleKey} className="bg-white/[0.05] backdrop-blur-sm rounded-2xl p-5 md:p-8 border border-purple-400/12 hover:border-purple-400/28 hover:bg-white/[0.07] transition-all cursor-default">
                  <i className={`${item.icon} text-3xl md:text-4xl mb-3 md:mb-4 text-purple-400 block`}></i>
                  <h4 className="text-lg md:text-xl font-bold mb-2 font-heading">{t(item.titleKey)}</h4>
                  <p className="text-white/50 text-sm md:text-base">{t(item.subKey)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="mb-10 md:mb-14">
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-8 md:mb-10 text-center font-heading">{t('productNarrative.visual_title')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {[
              { icon: 'ri-line-chart-line', titleKey: 'productNarrative.vis0_title', descKey: 'productNarrative.vis0_desc', stats: [{ labelKey: 'productNarrative.vis0_ai', val: '72%', color: 'text-purple-400' }, { labelKey: 'productNarrative.vis0_market', val: '60%', color: 'text-white/60' }, { labelKey: 'productNarrative.vis0_diff', val: '+12%', color: 'text-purple-400', border: true }] },
              { icon: 'ri-radar-line', titleKey: 'productNarrative.vis1_title', descKey: 'productNarrative.vis1_desc', stats: [{ labelKey: 'productNarrative.vis1_exposure', val: '< 5%', color: 'text-emerald-400' }, { labelKey: 'productNarrative.vis1_diversify', val: t('productNarrative.vis1_safeVal'), color: 'text-emerald-400' }, { labelKey: 'productNarrative.vis1_safe', val: t('productNarrative.vis1_safeVal'), color: 'text-purple-400', border: true }] },
              { icon: 'ri-refresh-line', titleKey: 'productNarrative.vis2_title', descKey: 'productNarrative.vis2_desc', stats: [{ labelKey: 'productNarrative.vis2_exec', val: t('productNarrative.vis2_execVal'), color: 'text-purple-400' }, { labelKey: 'productNarrative.vis2_auto', val: '100%', color: 'text-purple-400' }, { labelKey: 'productNarrative.vis2_compound', val: t('productNarrative.vis2_compoundVal'), color: 'text-purple-400', border: true }] },
            ].map(card => (
              <div key={card.titleKey} className="premium-card premium-card-hover rounded-2xl p-6 md:p-8">
                <div className="w-12 h-12 rounded-xl bg-purple-400/10 border border-purple-400/20 flex items-center justify-center mb-5">
                  <i className={`${card.icon} text-2xl text-purple-400`}></i>
                </div>
                <h4 className="text-lg md:text-xl font-bold text-white mb-3 font-heading">{t(card.titleKey)}</h4>
                <p className="text-white/45 mb-5 leading-relaxed text-sm">{t(card.descKey)}</p>
                <div className="space-y-2.5">
                  {card.stats.map(s => (
                    <div key={s.labelKey} className={`flex items-center justify-between text-sm ${s.border ? 'pt-2.5 border-t border-purple-500/10' : ''}`}>
                      <span className="text-white/35">{t(s.labelKey)}</span>
                      <span className={`font-bold ${s.color}`}>{s.val}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="premium-card border border-purple-400/12 rounded-2xl md:rounded-3xl p-8 md:p-12">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-5 font-heading">{t('productNarrative.core_title')}</h3>
            <p className="text-base md:text-xl text-white/45 leading-relaxed mb-8">
              {t('productNarrative.core_desc')}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {['productNarrative.core_item0','productNarrative.core_item1','productNarrative.core_item2','productNarrative.core_item3'].map(key => (
                <div key={key} className="flex flex-col items-center gap-2 bg-white/[0.03] border border-purple-500/8 rounded-xl p-4 md:p-5 hover:border-purple-400/22 transition-colors cursor-default">
                  <i className="ri-check-double-line text-2xl md:text-3xl text-purple-400"></i>
                  <span className="text-white/60 font-medium text-xs md:text-sm text-center">{t(key)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
